import { useEffect, useRef, useState } from 'react'
import { Alert, Button, Card, Dialog, DialogActions, DialogContent, DialogTitle, Skeleton, Stack, Typography } from '@mui/material'
import { DialogClose } from '@/components/DialogClose/DialogClose'
import { useNetwork } from '@/app/network/NetworkProvider'
import { formatPhone } from '@/components/PhoneNumberField/phoneNumbers'
import type { QueuedRegistration } from '@/lib/offline/offlineDb'
import { readMyContacts, saveMyContacts } from '../../myContactsCache'
import { outbox, type Outbox } from '../../outbox'
import { registrationService, RegistrationServiceError, type ConfirmationStatus, type RegisteredContact, type RegistrationService } from '../../registrationService'
import { useSync } from '../../SyncProvider'
import { TicketDialog } from '@/features/tickets/components/TicketDialog/TicketDialog'
import { ticketService, TicketServiceError, type TicketService } from '@/features/tickets/ticketService'
import { smsService, SmsServiceError, type SmsDeliveryStatus, type SmsService } from '@/features/sms/smsService'
import { RegistrationCard } from '../RegistrationCard/RegistrationCard'
import styles from './MyContactsPanel.module.scss'

/** `copiedAt` is set when the synced contacts come from the device's copy rather than the server. */
type State = Readonly<{ loading: boolean; synced: RegisteredContact[]; onDevice: QueuedRegistration[]; error: string | null; copiedAt: string | null }>

const timeFormat = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
const methodsOf = (contact: { phoneNumber?: string | null | undefined; emailAddress?: string | null | undefined; socialMedia?: string | null | undefined }) =>
  [formatPhone(contact.phoneNumber), contact.emailAddress, contact.socialMedia].filter((value): value is string => Boolean(value?.trim()))

// The card's pieces live with the card; re-exported for existing importers.
export { ConfirmationLabel, confirmationDetails, DeliveryLine } from '../RegistrationCard/RegistrationCard'

/** How long a text can wait in the queue before Send now is offered. */
const stuckAfterMs = 3 * 60_000

/**
 * Every contact the signed-in collector has registered at this event, across
 * all of its sessions: those still on this device first, then those the
 * server holds. Offline, the synced ones come from the copy saved the last
 * time the list loaded, so the whole list is still there.
 */
/** A registration the collector has asked to remove, waiting for them to confirm. */
export type Removing = Readonly<{ id: string; name: string; sent: boolean; ticketed: boolean }>

/** Asks before removing, and says what goes with it. A sent registration needs a connection; one still on this phone does not. */
export function RemoveDialog({ removing, pending, error, isOnline, onCancel, onConfirm }: Readonly<{ removing: Removing; pending: boolean; error: string | null; isOnline: boolean; onCancel: () => void; onConfirm: () => void }>) {
  const cancel = () => { if (!pending) onCancel() }
  const needsConnection = removing.sent && !isOnline
  return (
    <Dialog open onClose={cancel}>
      <DialogTitle>Remove {removing.name}?<DialogClose onClose={cancel} /></DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {removing.sent ? (
            <Typography>This removes their registration at this event: their details, answers and follow-up{removing.ticketed ? ', and their ticket, which stops working at the door' : ''}. It can’t be undone. A CSV someone already exported still has them.</Typography>
          ) : (
            <Typography>They haven’t been sent yet. This deletes their registration from this phone. It can’t be undone.</Typography>
          )}
          {error ? <Alert severity="error">{error}</Alert> : null}
          {needsConnection ? <Alert severity="warning">You’re offline. Reconnect to remove a registration that has already been sent.</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={cancel} disabled={pending}>Cancel</Button>
        <Button color="error" variant="outlined" disabled={pending || needsConnection} onClick={onConfirm}>{pending ? 'Removing…' : 'Remove registration'}</Button>
      </DialogActions>
    </Dialog>
  )
}

/** While All Mine is hidden, how often its list may be re-read from the server. */
export const hiddenRefreshGap = 60_000

export function MyContactsPanel({ eventId, collectorId, ticketed, service = registrationService, tickets = ticketService, sms = smsService, queue = outbox, active: visible = true, onCountChange }: { eventId: string; collectorId: string; ticketed: boolean; service?: Pick<RegistrationService, 'listMine'> & Partial<Pick<RegistrationService, 'updateConfirmationStatus' | 'remove'>>; tickets?: Pick<TicketService, 'issue' | 'resolvePublic'>; sms?: Pick<SmsService, 'readStatuses' | 'resend' | 'requestDrain'>; queue?: Pick<Outbox, 'forEvent'> & Partial<Pick<Outbox, 'withdraw'>>; active?: boolean; onCountChange?: (count: number | null) => void }) {
  const { changes } = useSync()
  const { isOnline } = useNetwork()
  const [state, setState] = useState<State>({ loading: true, synced: [], onDevice: [], error: null, copiedAt: null })
  const [reloadCount, setReloadCount] = useState(0)
  const [updatingIds, setUpdatingIds] = useState<ReadonlySet<string>>(() => new Set())
  const [updateError, setUpdateError] = useState<string | null>(null)
  const [ticketingIds, setTicketingIds] = useState<ReadonlySet<string>>(() => new Set())
  const [ticketError, setTicketError] = useState<string | null>(null)
  const [viewing, setViewing] = useState<Readonly<{ registrationId: string; name: string }> | null>(null)
  const [smsStatuses, setSmsStatuses] = useState<Readonly<Record<string, SmsDeliveryStatus>>>({})
  // When the statuses were read, to tell a text that has waited too long.
  const [statusesReadAt, setStatusesReadAt] = useState(0)
  const [statusReloads, setStatusReloads] = useState(0)
  const [nudgingIds, setNudgingIds] = useState<ReadonlySet<string>>(() => new Set())
  const [resendingIds, setResendingIds] = useState<ReadonlySet<string>>(() => new Set())
  const [smsError, setSmsError] = useState<string | null>(null)
  const [removing, setRemoving] = useState<Removing | null>(null)
  const [removePending, setRemovePending] = useState(false)
  const [removeError, setRemoveError] = useState<string | null>(null)

  // While All Mine is hidden (the Log tab is showing) the list only feeds the
  // tab's count, so the server is asked at most once a minute; registrations
  // waiting on this phone still update the count straight away.
  const lastRemoteLoad = useRef(0)
  const [throttleWake, setThrottleWake] = useState(0)
  useEffect(() => {
    let active = true
    const local = queue.forEvent(collectorId, eventId).catch(() => [] as QueuedRegistration[])
    const sinceRemote = Date.now() - lastRemoteLoad.current
    if (!visible && lastRemoteLoad.current > 0 && sinceRemote < hiddenRefreshGap) {
      void local.then((onDevice) => { if (active) setState((current) => ({ ...current, onDevice })) })
      const wake = window.setTimeout(() => setThrottleWake((count) => count + 1), hiddenRefreshGap - sinceRemote)
      return () => { active = false; window.clearTimeout(wake) }
    }
    lastRemoteLoad.current = Date.now()
    const remote = service.listMine(eventId, collectorId).then(
      (synced) => {
        // Kept so the same list is still there offline.
        void saveMyContacts(collectorId, eventId, synced).catch(() => undefined)
        return { synced, error: null as string | null, copiedAt: null as string | null }
      },
      async (cause: unknown) => {
        const error = cause instanceof RegistrationServiceError ? cause.message : 'Your contacts could not be loaded.'
        const copy = await readMyContacts(collectorId, eventId).catch(() => undefined)
        return { synced: copy?.contacts ?? [], error, copiedAt: copy?.cachedAt ?? null }
      },
    )
    void Promise.all([local, remote]).then(([onDevice, { synced, error, copiedAt }]) => {
      if (active) setState({ loading: false, synced, onDevice, error, copiedAt })
    })
    return () => { active = false }
  }, [eventId, collectorId, service, queue, reloadCount, changes, visible, throttleWake])

  useEffect(() => {
    onCountChange?.(state.loading ? null : state.synced.length + state.onDevice.length)
  }, [onCountChange, state.loading, state.onDevice.length, state.synced.length])

  // Read once for the whole list rather than per contact: the panel already
  // holds every registration id it needs, and an offline read simply leaves
  // the list without statuses rather than failing the load.
  // Text statuses are only shown in All Mine, so they're only read while it's open.
  useEffect(() => {
    if (!visible) return
    let active = true
    void sms.readStatuses(state.synced.map((contact) => contact.registrationId)).then(
      (statuses) => { if (active) { setSmsStatuses(statuses); setStatusesReadAt(Date.now()) } },
      () => { if (active) setSmsStatuses({}) },
    )
    return () => { active = false }
  }, [state.synced, sms, statusReloads, visible])

  if (state.loading) return <div role="status" aria-label="Loading your contacts"><Skeleton height={64} /><Skeleton height={64} /><Skeleton height={64} /></div>
  const total = state.synced.length + state.onDevice.length
  const retry = <Button size="small" onClick={() => { setState((current) => ({ ...current, loading: true })); setReloadCount((count) => count + 1) }}>Try again</Button>
  const updateStatus = async (registrationId: string, status: ConfirmationStatus) => {
    setUpdatingIds((current) => new Set(current).add(registrationId))
    setUpdateError(null)
    try {
      const saved = await (service.updateConfirmationStatus ?? registrationService.updateConfirmationStatus)(registrationId, status)
      setState((current) => {
        const synced = current.synced.map((contact) => contact.registrationId === registrationId ? { ...contact, confirmationStatus: saved } : contact)
        void saveMyContacts(collectorId, eventId, synced).catch(() => undefined)
        return { ...current, synced }
      })
    } catch (cause) {
      setUpdateError(cause instanceof Error ? cause.message : 'The confirmation status could not be saved. Please try again.')
    } finally {
      setUpdatingIds((current) => {
        const next = new Set(current)
        next.delete(registrationId)
        return next
      })
    }
  }
  const issueTicket = async (registrationId: string) => {
    setTicketingIds((current) => new Set(current).add(registrationId))
    setTicketError(null)
    try {
      const ticket = await tickets.issue(registrationId)
      setState((current) => {
        const synced = current.synced.map((contact) => contact.registrationId === registrationId ? { ...contact, ticketReference: ticket.reference } : contact)
        void saveMyContacts(collectorId, eventId, synced).catch(() => undefined)
        return { ...current, synced }
      })
      // A new ticket queues its text; send it now rather than at the next
      // scheduled run.
      void sms.requestDrain().catch(() => undefined)
    } catch (cause) {
      setTicketError(cause instanceof TicketServiceError ? cause.message : 'The ticket could not be issued. Please try again.')
    } finally {
      setTicketingIds((current) => {
        const next = new Set(current)
        next.delete(registrationId)
        return next
      })
    }
  }

  // Send now only asks the sender to run; it never adds a text, so it can't
  // put a second message in front of the attendee.
  const sendNow = async (registrationId: string) => {
    setNudgingIds((current) => new Set(current).add(registrationId))
    setSmsError(null)
    try {
      await sms.requestDrain()
    } catch {
      setSmsError('The message couldn’t be sent right now. It stays queued and goes out automatically.')
    } finally {
      setNudgingIds((current) => {
        const next = new Set(current)
        next.delete(registrationId)
        return next
      })
      setStatusReloads((count) => count + 1)
    }
  }

  const resendMessage = async (registrationId: string) => {
    setResendingIds((current) => new Set(current).add(registrationId))
    setSmsError(null)
    try {
      await sms.resend(registrationId)
      setSmsStatuses((current) => ({
        ...current,
        [registrationId]: { state: 'sending', reason: null, at: new Date().toISOString(), canResend: false, queuedSince: new Date().toISOString() },
      }))
      // The new attempt is queued, so ask for delivery now rather than waiting
      // for the next sync. A failed request just leaves it queued for a later
      // one, which is why it is not allowed to surface as an error here.
      void sms.requestDrain().catch(() => undefined)
    } catch (cause) {
      setSmsError(cause instanceof SmsServiceError ? cause.message : 'The message could not be resent. Please try again.')
    } finally {
      setResendingIds((current) => {
        const next = new Set(current)
        next.delete(registrationId)
        return next
      })
    }
  }
  const askToRemove = (next: Removing) => { setRemoveError(null); setRemoving(next) }
  const remove = async () => {
    if (!removing) return
    setRemovePending(true)
    setRemoveError(null)
    try {
      if (removing.sent) {
        await (service.remove ?? registrationService.remove)(removing.id)
        setState((current) => {
          const synced = current.synced.filter((contact) => contact.registrationId !== removing.id)
          void saveMyContacts(collectorId, eventId, synced).catch(() => undefined)
          return { ...current, synced }
        })
      } else {
        const withdrawn = await (queue.withdraw ?? outbox.withdraw)(removing.id)
        if (!withdrawn) {
          setRemoveError('They’re being sent right now. Try again in a moment, once they’ve synced.')
          return
        }
        setState((current) => ({ ...current, onDevice: current.onDevice.filter((record) => record.clientRegistrationId !== removing.id) }))
      }
      setRemoving(null)
    } catch (cause) {
      setRemoveError(cause instanceof Error ? cause.message : 'The registration could not be removed. Please try again.')
    } finally {
      setRemovePending(false)
    }
  }
  const removeButton = (next: Removing) => (
    <Button size="small" className={styles.remove} aria-label={`Remove ${next.name}`} onClick={() => askToRemove(next)}>Remove</Button>
  )

  if (total === 0 && state.error) return <Alert severity="error" action={retry}>{state.error}</Alert>
  if (total === 0) return <Card className={styles.empty}><Typography>No contacts yet.</Typography><Typography color="text.secondary">People you register at this event, in any of its sessions, appear here.</Typography></Card>

  return (
    <div className={styles.panel}>
      {state.error && !state.copiedAt ? (
        <Typography className={styles.count}>
          {total === 1 ? '1 contact' : `${total} contacts`} on this device.{' '}
          <span className={styles.note}>{isOnline ? <>Synced contacts couldn’t load. {retry}</> : 'Synced contacts appear when you’re back online.'}</span>
        </Typography>
      ) : (
        <Typography className={styles.count}>
          {total === 1 ? '1 contact' : `${total} contacts`} across this event's sessions
          {state.copiedAt ? <span className={styles.note}>{' '}· as of {timeFormat.format(new Date(state.copiedAt))}{isOnline ? <>. {retry}</> : null}</span> : null}
        </Typography>
      )}
      {updateError ? <Alert severity="error">{updateError}</Alert> : null}
      {ticketError ? <Alert severity="error">{ticketError}</Alert> : null}
      {smsError ? <Alert severity="error">{smsError}</Alert> : null}
      <ul className={styles.list}>
        {state.onDevice.map((record) => {
          const methods = methodsOf(record.contact)
          return (
            <li key={record.clientRegistrationId}>
              <Card className={styles.contact}>
                <span className={styles.nameRow}>
                  <span className={styles.name}>{[record.contact.firstName, record.contact.lastName].filter(Boolean).join(' ')}</span>
                  <span className={record.status === 'error' ? styles.attention : styles.waiting}>{record.status === 'error' ? 'Needs attention' : 'Waiting to sync'}</span>
                </span>
                {methods.length > 0 ? <span className={styles.methods}>{methods.join(' · ')}</span> : null}
                <span className={styles.meta}>{timeFormat.format(new Date(record.capturedAt))}</span>
                <span className={styles.ticketRow}>
                  {removeButton({ id: record.clientRegistrationId, name: [record.contact.firstName, record.contact.lastName].filter(Boolean).join(' '), sent: false, ticketed: false })}
                </span>
              </Card>
            </li>
          )
        })}
        {state.synced.map((contact) => {
          const methods = methodsOf(contact)
          const delivery = smsStatuses[contact.registrationId] ?? null
          // Resend only where trying again could help; the server works it out.
          const canResend = delivery?.canResend === true
          // A text queued for more than a few minutes should have gone by now.
          const queuedSince = delivery?.queuedSince
          const stuck = queuedSince !== null && queuedSince !== undefined && Date.parse(queuedSince) < statusesReadAt - stuckAfterMs
          // At a ticketed event, someone with a ticket and no phone number gets
          // no text; say so, so nobody waits for one.
          const noPhone = ticketed && Boolean(contact.ticketReference) && !contact.phoneNumber && !delivery
          return (
            <li key={contact.registrationId}>
              <RegistrationCard
                name={[contact.firstName, contact.lastName].filter(Boolean).join(' ')}
                online={isOnline}
                outcome={{ value: contact.confirmationStatus, saving: updatingIds.has(contact.registrationId), onChange: (next) => void updateStatus(contact.registrationId, next) }}
                methods={methods.length > 0 ? methods.join(' · ') : null}
                meta={[contact.sessionName, timeFormat.format(new Date(contact.capturedAt))].filter(Boolean).join(' · ')}
                leading={removeButton({ id: contact.registrationId, name: [contact.firstName, contact.lastName].filter(Boolean).join(' '), sent: true, ticketed: Boolean(contact.ticketReference) })}
                ticket={ticketed ? {
                  reference: contact.ticketReference,
                  onView: () => setViewing({ registrationId: contact.registrationId, name: [contact.firstName, contact.lastName].filter(Boolean).join(' ') }),
                  issue: { busy: ticketingIds.has(contact.registrationId), onClick: () => void issueTicket(contact.registrationId) },
                } : null}
                delivery={delivery}
                noPhone={noPhone}
                resend={canResend ? { busy: resendingIds.has(contact.registrationId), onClick: () => void resendMessage(contact.registrationId) } : null}
                sendNow={stuck ? { busy: nudgingIds.has(contact.registrationId), onClick: () => void sendNow(contact.registrationId) } : null}
              />
            </li>
          )
        })}
      </ul>
      {removing ? <RemoveDialog removing={removing} pending={removePending} error={removeError} isOnline={isOnline} onCancel={() => setRemoving(null)} onConfirm={() => void remove()} /> : null}
      {viewing ? <TicketDialog registrationId={viewing.registrationId} attendeeName={viewing.name} onClose={() => setViewing(null)} service={tickets} /> : null}
    </div>
  )
}
